import { default as React } from 'react';
import { BaseEditIcon } from '../BaseEditIcon';
import { ZpaEditDialog } from './ZpaEditDialog';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import { WorkloadClient } from '@internal/workload-api-helper';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import { Account, Features } from '../types';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

let accountObject: { [key: string]: string | undefined };

type Zpa = {
  enabled: boolean;
};

interface ZpaEditIconProps {
  variant: string | undefined;
  classes: ClassNameMap;
  entity: Entity;
  features: Features | undefined;
}

// Main component
export const ZpaEditIcon = ({
  variant,
  classes,
  entity,
  features,
}: ZpaEditIconProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const identity = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);

  const [workloadData, setWorkloadData] = useState<Account>();
  const [requesterEmail, setRequesterEmail] = useState('');
  const accountId: string | undefined =
    entity?.metadata?.annotations?.['amazonaws.com/account-id'];
  const zpa: Zpa | undefined = features?.zpa as Zpa | undefined;

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const fetchWorkloadData = async () => {
    return await workloadClient.apiFetch(`workloads/${accountId}`);
  };

  useAsync(async (): Promise<Account> => {
    const [workloadDetails] = await Promise.all([fetchWorkloadData()]);

    const [details] = await Promise.all([workloadDetails.json()]);
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);

    return details;
  }, []);

  const patchResourceData = async ({
    workload,
    resourceName,
    payload,
  }: {
    workload?: string;
    resourceName?: string;
    payload: { [_: string]: Object };
  }) => {
    return workloadClient.apiFetch(
      `/workloads/workload/${workload}/resource/${resourceName}`,
      'PATCH',
      payload,
    );
  };

  const parseWorkloadObjectValues = (
    catalogLocationStringList: string[] | undefined,
  ): { [key: string]: string | undefined } => {
    const resourceFileName: string | undefined =
      catalogLocationStringList?.pop();
    const resourceName = resourceFileName?.split('.')[0];
    const workload = catalogLocationStringList?.pop();

    return {
      resourceName,
      workload,
    };
  };

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const callPatchApi = async (updated_zpa: {}) => {
    const payload: { [key: string]: Object } = updated_zpa;

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve(false);
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await patchResourceData({
          workload: accountObject.workload,
          resourceName: accountObject.resourceName,
          payload,
        });

        if (!isOkayResponse(patchResourceDataResponse)) {
          stopLoadingAndCloseModal();
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        } else {
          alertApi.post({
            message: getMessage({
              successMessage: 'Updating ZPA information.',
            }).SUCCESS,
            severity: SeverityEnum.INFO,
          });

          await catalogApi.refreshEntity(stringifyEntityRef(entity));
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }
    return Promise.resolve(true);
  };
  const [loading, setLoading] = React.useState(false);

  const title = `Enable Zscaler Private Access`;

  const [zpaEnabled, setZpaEnabled] = React.useState(zpa?.enabled);
  function updateZpaEnabled() {
    setZpaEnabled(!zpaEnabled);
  }

  async function handleConfirm(e: any) {
    e.preventDefault();
    const zpa_config = zpa;
    const updated_zpa_config = { zpa: { enabled: zpaEnabled } };

    if (!_.isEqual(zpa_config, updated_zpa_config)) {
      setLoading(true);
      await callPatchApi(updated_zpa_config);
    }
    setLoading(false);
    handleClose();
  }
  return (
    <BaseEditIcon
      workloadData={workloadData}
      requesterEmail={requesterEmail}
      handleClickOpen={handleClickOpen}
      isModalOpen={isModalOpen}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      disableConfirm={zpaEnabled === zpa?.enabled}
      isLoading={loading}
      isLoadingEdit={false}
      dialogTitle={title}
      settingName="Zscaler Private Access"
      allowEngineerEdit
      apiValidationPayload
    >
      <ZpaEditDialog
        cardClass={cardClass}
        cardContentClass={cardContentClass}
        zpaEnabled={zpaEnabled}
        updateZpaEnabled={updateZpaEnabled}
      />
    </BaseEditIcon>
  );
};
